import { SummaryPanelView } from '../view/SummaryPanelView';
import { templateSummaryPanel } from '../view/template';
import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { SummaryPanelWebComponent } from '../index';
import { SummaryPanelWebComponentSchema } from '../types';
import { getSeverityScoreClass } from '../../../utils/xdr.utils';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { getFormattedDateTime, TimestampFormat } from '@nirvana/shared/time';
import { downloadBlob } from '../../../utils/files.utils';
import { getRequestDefaultOptions } from '../../../utils/request.utils';

export class SummaryPanelController extends BaseWebComponentController {
  public component: SummaryPanelWebComponent;
  public view: SummaryPanelView;
  public viewConstructor: typeof SummaryPanelView;

  constructor(component: SummaryPanelWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: SummaryPanelView,
      viewTemplate: templateSummaryPanel,
    });
  }

  async getDataForView(): Promise<SummaryPanelWebComponentSchema> {
    return {
      incidentId: this.component.incidentId,
      incidentScore: this.component.incidentScore,
      incidentCompanyName: this.component.incidentCompanyName,
      incidentNumber: this.component.incidentNumber,
      severityColor: getSeverityScoreClass(this.component.incidentScore),
      created: this.component.created,
      lastUpdated: this.component.lastUpdated,
      attackTypes: this.component.attackTypes,
      narrative: this.component.narrative,
      reasoning: this.component.reasoning,
      summaryMonitoring: this.component.summaryMonitoring,
    };
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        event: 'click',
        element: this.view.dom.$otherAttackTypesText,
        callback: this.toggleMoreAttackTypes,
      },
      {
        event: 'click',
        element: this.view.dom.$moreAttackTypesCloseButton,
        callback: this.toggleMoreAttackTypes,
      },
      {
        event: 'click',
        element: this.view.dom.$incidentReportBtn,
        callback: this.handleIncidentReportPdf,
      }
    ];
  }

  public toggleMoreAttackTypes() {
    this.view.dom.$moreAttackTypesCard.classList.toggle('shown');
  }

  private async handleIncidentReportPdf() {
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: this.component.dependencies.translateFn('Your PDF is being generated.'),
      severity: ToastSeverity.SUCCESS
    });
    this.view.dom.$incidentReportBtn.setLoadingState(true);

    try {
      const client = await this.component.dependencies.acquireReportClientSession();
      const response = await client.getIncidentReportPdf(
        {
          incidentId: this.component.incidentId
        },
        getRequestDefaultOptions()
      ).toPromise();

      const fileBlob = new Blob([response.pdf], { type: 'application/pdf' });

      if (!fileBlob) {
        throw new Error('Failed to create PDF blob');
      }

      downloadBlob(fileBlob, response.name);

      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.component.dependencies.translateFn('Incident report PDF was generated successfully.'),
        severity: ToastSeverity.SUCCESS
      });
    } catch (e) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.component.dependencies.translateFn('Failed to generate the incident report PDF. If the error persists, contact Technical Support. '),
        severity: ToastSeverity.ERROR
      });
    } finally {
      this.view.dom.$incidentReportBtn.setLoadingState(false);
    }
  }
}
